import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { X, Mail } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ShareModal({ listId, type, onClose, onShared }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    try {
      const endpoint = type === 'task'
        ? `${API}/task-lists/${listId}/share`
        : `${API}/reward-lists/${listId}/share`;
      
      await axios.post(endpoint, { email }, { withCredentials: true });
      toast.success(t('messages.listShared', { email }));
      setEmail('');
      onShared();
    } catch (error) {
      console.error('Error sharing list:', error);
      toast.error(t('messages.errorSharingList'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50" onClick={onClose}>
      <div
        className="bg-white dark:bg-zinc-800 border-t-2 sm:border-2 border-[#18181B] dark:border-zinc-600 rounded-t-2xl sm:rounded-2xl hard-shadow p-4 sm:p-6 w-full sm:max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="share-modal"
      >
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-zinc-900 dark:text-white">{t('modals.shareList')}</h2>
          <button
            onClick={onClose}
            className="rounded-full p-1.5 sm:p-2 hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
            data-testid="close-modal-btn"
          >
            <X className="w-5 h-5 text-zinc-900 dark:text-white" />
          </button>
        </div>

        <div className="mb-4 sm:mb-6">
          <p className="text-xs sm:text-sm text-zinc-600 dark:text-zinc-300">
            {t('modals.shareDescription')}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4 sm:mb-6">
            <label className="block text-xs sm:text-sm font-bold mb-1.5 sm:mb-2 text-zinc-900 dark:text-white">{t('modals.userEmail')}</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-zinc-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('modals.emailPlaceholder')}
                className="w-full h-10 sm:h-12 rounded-lg sm:rounded-xl border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 pl-10 sm:pl-11 pr-3 sm:pr-4 text-sm sm:text-base text-zinc-900 dark:text-white placeholder-zinc-400 focus:outline-none focus:shadow-[4px_4px_0px_0px_rgba(24,24,27,1)] transition-shadow"
                data-testid="email-input"
                autoFocus
              />
            </div>
          </div>

          <div className="flex gap-2 sm:gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-full border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 text-zinc-900 dark:text-white font-bold py-2.5 sm:py-3 px-4 sm:px-6 text-sm sm:text-base hover:bg-zinc-100 dark:hover:bg-zinc-600 hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] active:translate-y-1 active:shadow-none transition-all"
              data-testid="cancel-btn"
            >
              {t('modals.cancel')}
            </button>
            <button
              type="submit"
              disabled={!email.trim() || loading}
              className="flex-1 rounded-full border-2 border-[#18181B] bg-[#18181B] text-white font-bold py-2.5 sm:py-3 px-4 sm:px-6 text-sm sm:text-base hover:bg-[#333] hover:shadow-[2px_2px_0px_0px_rgba(24,24,27,1)] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="share-btn"
            >
              {loading ? t('modals.sharing') : t('modals.share')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
